import API from "./axiosConfig";

const setAuthHeader = () => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    API.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
};

// --- GET cache & de-duplication (prevent duplicate requests) ---
const productsCache = new Map();
const productsInFlight = new Map();
const PRODUCTS_TTL_MS = 2 * 60 * 1000; // 2 minutes

export const clearProductsCache = () => {
  productsCache.clear();
  console.log("[Cache] Products cache cleared.");
};

const cachedProductsGet = async (url, params = {}, ttlMs = PRODUCTS_TTL_MS) => {
  setAuthHeader();
  
  const queryString = new URLSearchParams(params).toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;
  const key = `GET ${fullUrl}`;
  const now = Date.now();

  const cached = productsCache.get(key);
  if (cached && now - cached.time < ttlMs) {
    return cached.response;
  }

  if (productsInFlight.has(key)) {
    return productsInFlight.get(key);
  }

  const requestPromise = API
    .get(url, { params })
    .then((response) => {
      productsCache.set(key, { response, time: Date.now() });
      productsInFlight.delete(key);
      return response;
    })
    .catch((err) => {
      productsInFlight.delete(key);
      throw err;
    });

  productsInFlight.set(key, requestPromise);
  return requestPromise;
};

// === Product Type Endpoints ===
export const getProductTypes = async () => {
  setAuthHeader();
  return await API.get("api/product-types/");
};

export const createProductType = async (typeData) => {
  setAuthHeader();
  return await API.post("api/product-types/", typeData);
};

export const getProductTypeById = async (id) => {
  setAuthHeader();
  return await API.get(`api/product-types/${id}/`);
};
//Get Product Variations by  By product type id
export const getProductVariationsByProductType = async (productTypeId, brandId) => {
  setAuthHeader();
  return await API.get(`/api/product-variations/by-product-type/`, {
    params: { product_type_id: productTypeId, brand_id: brandId },
  });
};

// === Product Universe Endpoints ===
export const getProductUniverses = async () => {
  setAuthHeader();
  return await API.get("api/product-universes/");
};

export const createProductUniverse = async (universeData) => {
  setAuthHeader();
  return await API.post("api/product-universes/", universeData);
};

export const getProductUniverseById = async (id) => {
  setAuthHeader();
  return await API.get(`api/product-universes/${id}/`);
};

// === Product Endpoints ===
export const createProduct = async (productData) => {
  setAuthHeader();
  return await API.post("api/products/", productData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getProducts = async () => {
  setAuthHeader();
  return await API.get("api/products/");
};
export const getProductsByClient = async (clientId) => {
  setAuthHeader();

  if (!clientId) {
    throw new Error("clientId is required to fetch products by client.");
  }

  return await API.get(`/api/products/by-client/?client_id=${clientId}`);
};

export const getProductById = async (id) => {
  setAuthHeader();
  return await API.get(`api/products/${id}/`);
};

export const updateProduct = async (id, productData) => {
  setAuthHeader();
  return await API.put(`api/products/${id}/`, productData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const deleteProduct = async (id) => {
  setAuthHeader();
  return await API.delete(`api/products/${id}/`);
};

// === Product Variation Endpoints ===
export const createProductVariation = async (variationData) => {
  setAuthHeader();
  return await API.post("api/product-variations/", variationData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getProductVariations = async () => {
  setAuthHeader();
  return await API.get("api/product-variations/");
};

export const getProductVariationById = async (id) => {
  setAuthHeader();
  return await API.get(`api/product-variations/${id}/`);
};

export const updateProductVariation = async (id, variationData) => {
  setAuthHeader();
  return await API.put(`api/product-variations/${id}/`, variationData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const deleteProductVariation = async (id) => {
  setAuthHeader();
  return await API.delete(`api/product-variations/${id}/`);
};

export const getProductVariationsByProductId = async (productId) => {
  setAuthHeader();
  return await API.get(`/api/product-variations/all-by-product/${productId}/`);
};
export const getConfirmedProductVariationsByProductId = async (productId) => {
  setAuthHeader();
  return await API.get(`/api/product-variations/confirmed-by-product/${productId}/`);
};

// === Product Variation Type Endpoints ===
export const getProductVariationTypes = async () => {
  setAuthHeader();
  return await API.get("/api/product-variation-types/");
};

export const createProductVariationType = async (variationData) => {
  setAuthHeader();
  return await API.post("/api/product-variation-types/", variationData);
};

// API function to upload a single image for a product variation
export const uploadVariationImage = async (variationId, imageFile) => {
  const formData = new FormData();
  formData.append("product_variation", variationId);
  formData.append("image", imageFile);
  setAuthHeader();
  return await API.post("/api/variation-images/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

//API function to delete variation image from data base
export const deleteVariationImage = async (imageId) => {
  setAuthHeader();
  return await API.delete(`/api/variation-images/${imageId}/`);
};

export const getImagesByVariationId = async (variationId) => {
  setAuthHeader();
  return await API.get(`/api/variation-images/?product_variation=${variationId}`);
};

// === Product Order Endpoints ===
export const getProductOrders = async () => {
  setAuthHeader();
  return await API.get(`/api/product-orders/`);
};

export const getProductOrderById = async (id) => {
  setAuthHeader();
  return await API.get(`/api/product-orders/${id}/`);
};

export const createProductOrder = async (orderData) => {
  setAuthHeader();
  return await API.post(`/api/product-orders/`, orderData);
};

export const updateProductOrder = async (id, orderData) => {
  setAuthHeader();
  return await API.put(`/api/product-orders/${id}/`, orderData);
};

export const deleteProductOrder = async (id) => {
  setAuthHeader();
  return await API.delete(`/api/product-orders/${id}/`);
};

// === Order Status Endpoints ===
export const getOrderStatuses = async () => {
  setAuthHeader();
  return await API.get(`/api/order-statuses/`);
};

// === Receiving Endpoints ===
export const getReceivings = async () => {
  setAuthHeader();
  return await API.get(`/api/receivings/`);
};

export const getReceivingById = async (id) => {
  setAuthHeader();
  return await API.get(`/api/receivings/${id}/`);
};

export const createReceiving = async (receivingData) => {
  setAuthHeader();
  return await API.post(`/api/receivings/`, receivingData);
};

export const updateReceiving = async (id, receivingData) => {
  setAuthHeader();
  return await API.put(`/api/receivings/${id}/`, receivingData);
};

export const deleteReceiving = async (id) => {
  setAuthHeader();
  return await API.delete(`/api/receivings/${id}/`);
};

// === Shipment Endpoints ===
export const getShipments = async () => {
  setAuthHeader();
  return await API.get(`/api/shipments/`);
};

export const getShipmentById = async (id) => {
  setAuthHeader();
  return await API.get(`/api/shipments/${id}/`);
};

export const createShipment = async (shipmentData) => {
  setAuthHeader();
  return await API.post(`/api/shipments/`, shipmentData);
};

export const updateShipment = async (id, shipmentData) => {
  setAuthHeader();
  return await API.put(`/api/shipments/${id}/`, shipmentData);
};

export const deleteShipment = async (id) => {
  setAuthHeader();
  return await API.delete(`/api/shipments/${id}/`);
};

// === Shipment Tracking File Endpoints ===
export const uploadShipmentTrackingFile = async (shipmentId, file) => {
  const formData = new FormData();
  formData.append("shipment", shipmentId);
  formData.append("file", file);
  setAuthHeader();
  return await API.post(`/api/shipment-tracking-files/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const deleteShipmentTrackingFile = async (fileId) => {
  setAuthHeader();
  return await API.delete(`/api/shipment-tracking-files/${fileId}/`);
};

// === Order File Endpoints ===
export const uploadOrderFile = async (orderId, file) => {
  const formData = new FormData();
  formData.append("order", orderId);
  formData.append("file", file);
  setAuthHeader();
  return await API.post(`/api/order-files/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const deleteOrderFile = async (fileId) => {
  setAuthHeader();
  return await API.delete(`/api/order-files/${fileId}/`);
};

// === Product Variation with Inventory Endpoint ===
export const fetchProductVariationsWithInventory = async (clientIds, page = 1, pageSize = 25) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (clientIds && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  }

  // Add pagination parameters
  params.append("page", page);
  params.append("page_size", pageSize);

  return await API.get(`/api/product-variations-with-inventory/?${params.toString()}`);
};

// === Product Variation Status Endpoint ===
export const fetchProductVariationStatus = async (
  clientIds,
  page = 1,
  pageSize = 25,
  searchQuery = ""
) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (clientIds && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  }

  // Add pagination parameters
  params.append("page", page);
  params.append("page_size", pageSize);

  // Add search parameter if provided
  if (searchQuery && searchQuery.trim()) {
    params.append("search", searchQuery.trim());
  }

  return await API.get(`/api/product-variations-status/?${params.toString()}`);
};

// === Products by Product Type Endpoint ===
export const getProductsByProductType = async (productTypeId, brandId) => {
  setAuthHeader();
  return await API.get(`/api/products/by-product-type/`, {
    params: { product_type_id: productTypeId, brand_id: brandId },
  });
};

// === Order List Endpoint ===
export const fetchOrderList = async (clientIds, page = 1, pageSize = 25) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (clientIds && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  }

  params.append("page", page);
  params.append("page_size", pageSize);

  return await API.get(`/api/orders-list/?${params.toString()}`);
};

// === Product Order List Endpoint ===
export const fetchProductOrderList = async (clientIds, page = 1, pageSize = 25) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (clientIds && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  }

  params.append("page", page);
  params.append("page_size", pageSize);

  return await API.get(`/api/product-orders-list/?${params.toString()}`);
};

// === Print Order List Endpoint ===
export const fetchPrintOrderList = async (clientIds, page = 1, pageSize = 25) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (clientIds && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  }

  params.append("page", page);
  params.append("page_size", pageSize);

  return await API.get(`/api/print-orders-list/?${params.toString()}`);
};

export const updateInventory = async (id, inventoryData) => {
  setAuthHeader();
  return await API.put(`/api/inventories/${id}/`, inventoryData);
};

export const getOrCreateInventory = async (warehouseId, productVariationId, defaultData = {}) => {
  setAuthHeader();
  return await API.post(`/api/inventories/get-or-create/`, {
    warehouse_id: warehouseId,
    product_variation_id: productVariationId,
    ...defaultData,
  });
};
export const getProductOrderItemsByVariation = async (productVariationId) => {
  setAuthHeader();
  return await API.get(`/api/order-items/`, {
    params: {
      product_variation: productVariationId,
    },
  });
};

// Print Categories
export const getPrintCategories = async () => {
  setAuthHeader();
  return await API.get(`/api/print-categories/`);
};

export const createPrintCategory = async (categoryData) => {
  setAuthHeader();
  return await API.post(`/api/print-categories/`, categoryData);
};

export const updatePrintCategory = async (id, categoryData) => {
  setAuthHeader();
  return await API.put(`/api/print-categories/${id}/`, categoryData);
};

export const deletePrintCategory = async (id) => {
  setAuthHeader();
  return await API.delete(`/api/print-categories/${id}/`);
};

// Prints
export const getPrints = async () => {
  setAuthHeader();
  return await API.get(`/api/prints/`);
};
export const getPrint = async (id) => {
  setAuthHeader();
  return await API.get(`/api/prints/${id}/`);
};

export const createPrint = async (printData) => {
  setAuthHeader();
  return await API.post(`/api/prints/`, printData);
};

export const updatePrint = async (id, printData) => {
  setAuthHeader();
  return await API.patch(`/api/prints/${id}/`, printData);
};

export const deletePrint = async (id) => {
  setAuthHeader();
  return await API.delete(`/api/prints/${id}/`);
};

export const getPrintStocks = async (clientIds = []) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (clientIds && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  }

  return await API.get(`/api/print-stocks/?${params.toString()}`);
};
// === Print Order Endpoints ===
export const getPrintOrders = async () => {
  setAuthHeader();
  return await API.get(`/api/print-orders/`);
};

export const getPrintOrderById = async (id) => {
  setAuthHeader();
  return await API.get(`/api/print-orders/${id}/`);
};

export const createPrintOrder = async (orderData) => {
  setAuthHeader();
  return await API.post(`/api/print-orders/`, orderData);
};

export const updatePrintOrder = async (id, orderData) => {
  setAuthHeader();
  return await API.put(`/api/print-orders/${id}/`, orderData);
};

export const deletePrintOrder = async (id) => {
  setAuthHeader();
  return await API.delete(`/api/print-orders/${id}/`);
};

// === Print Order Files Endpoints ===
export const uploadPrintOrderFile = async (printOrderId, file) => {
  const formData = new FormData();
  formData.append("print_order", printOrderId);
  formData.append("file", file);
  setAuthHeader();
  return await API.post(`/api/print-order-files/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const deletePrintOrderFile = async (fileId) => {
  setAuthHeader();
  return await API.delete(`/api/print-order-files/${fileId}/`);
};
// === Print Order Endpoints === by-offer-and-printer
export const getPrintOrdersByOffer = async (offerId) => {
  setAuthHeader();
  return await API.get(`/api/print-orders/by-offer/`, {
    params: { offer_id: offerId },
  });
};

// === Print Order Endpoints === by-offer-and-printer
export const getPrintOrdersByOfferAndPrinter = async (offerId, printerId) => {
  setAuthHeader();
  return await API.get(`/api/print-orders/by-offer-and-printer/`, {
    params: { offer_id: offerId, printer_id: printerId },
  });
};

// === Products by Brand Endpoint === CACHED
export const getProductsByBrand = async (brandId) => {
  if (!brandId) {
    throw new Error("brandId is required to fetch products by brand.");
  }
  return await cachedProductsGet(`/api/products/by-brand/`, { brand_id: brandId });
};

// === Product Variations by Product Brand Endpoint === CACHED
export const getProductVariationsByProductBrand = async (brandId) => {
  if (!brandId) {
    throw new Error("brandId is required to fetch product variations by product brand.");
  }
  return await cachedProductsGet(`/api/product-variations/by-product-brand/`, { brand_id: brandId });
};

// === Order Item Endpoints ===
export const getOrderItems = async () => {
  setAuthHeader();
  return await API.get(`/api/order-items/`);
};

export const getOrderItemById = async (id) => {
  setAuthHeader();
  return await API.get(`/api/order-items/${id}/`);
};

export const createOrderItem = async (orderItemData) => {
  setAuthHeader();
  return await API.post(`/api/order-items/`, orderItemData);
};

export const updateOrderItem = async (id, orderItemData) => {
  setAuthHeader();
  return await API.put(`/api/order-items/${id}/`, orderItemData);
};

export const deleteOrderItem = async (id) => {
  setAuthHeader();
  return await API.delete(`/api/order-items/${id}/`);
};

// === Order Item Receivings ===
export const getOrderItemReceivings = async (orderItemId) => {
  setAuthHeader();
  return await API.get(`/api/order-items/${orderItemId}/receivings/`);
};

// === Order Item Shipments ===
export const getOrderItemShipments = async (orderItemId) => {
  setAuthHeader();
  return await API.get(`/api/order-items/${orderItemId}/shipments/`);
};

// === Print Order Item Endpoints ===
export const getPrintOrderItems = async () => {
  setAuthHeader();
  return await API.get(`/api/print-order-items/`);
};

export const getPrintOrderItemById = async (id) => {
  setAuthHeader();
  return await API.get(`/api/print-order-items/${id}/`);
};

export const createPrintOrderItem = async (orderItemData) => {
  setAuthHeader();
  return await API.post(`/api/print-order-items/`, orderItemData);
};

export const updatePrintOrderItem = async (id, orderItemData) => {
  setAuthHeader();
  return await API.put(`/api/print-order-items/${id}/`, orderItemData);
};

export const deletePrintOrderItem = async (id) => {
  setAuthHeader();
  return await API.delete(`/api/print-order-items/${id}/`);
};

// === Envelope Stock Endpoints ===
export const getIndependentEnvelopeStocks = async (client_ids = []) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (client_ids.length > 0) {
    params.append("client_ids", client_ids.join(","));
  }

  return await API.get(`/api/independent-envelope-stocks/?${params.toString()}`);
};

export const createProductPricing = async (pricingData) => {
  setAuthHeader();
  return await API.post(`/api/product-pricings/`, pricingData);
};

export const updateProductPricing = async (id, pricingData) => {
  setAuthHeader();
  return await API.put(`/api/product-pricings/${id}/`, pricingData);
};

export const deleteProductPricing = async (id) => {
  setAuthHeader();
  return await API.delete(`/api/product-pricings/${id}/`);
};

export const getProductPricingByProductVariation = async (productVariationId) => {
  setAuthHeader();
  return await API.get(`/api/product-pricings/by-product-variation/`, {
    params: {
      product_variation_id: productVariationId,
    },
  });
};

// === Variation Pricing Endpoints ===
export const getVariationPricing = async (variationId) => {
  setAuthHeader();
  return await API.get(`/api/product-pricings/by-product-variation/`, {
    params: {
      product_variation_id: variationId,
    },
  });
};

export const createVariationPricing = async (pricingData) => {
  setAuthHeader();
  return await API.post(`/api/product-pricings/`, pricingData);
};

export const updateVariationPricing = async (id, pricingData) => {
  setAuthHeader();
  return await API.put(`/api/product-pricings/${id}/`, pricingData);
};

export const deleteVariationPricing = async (id) => {
  setAuthHeader();
  return await API.delete(`/api/product-pricings/${id}/`);
};

export const getInventoryByVariations = async (variationIds = []) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (variationIds && variationIds.length > 0) {
    variationIds.forEach((id) => params.append("variation_ids[]", id));
  }

  return await API.get(`/api/inventories/by-variations/?${params.toString()}`);
};

export const getProductDashboardData = async (clientIds = [], startDate, endDate) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (clientIds && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  }

  if (startDate) {
    params.append("start_date", startDate);
  }

  if (endDate) {
    params.append("end_date", endDate);
  }

  return await API.get(`/api/product-dashboard/?${params.toString()}`);
};

export const getPurchaseOrderEvolution = async (clientIds = [], variationIds = []) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (clientIds && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  }

  if (variationIds && variationIds.length > 0) {
    variationIds.forEach((id) => params.append("variation_ids[]", id));
  }

  return await API.get(`/api/purchase-order-evolution/?${params.toString()}`);
};

export const getPrintStockByOffers = async (offerIds = []) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (offerIds && offerIds.length > 0) {
    offerIds.forEach((id) => params.append("offer_ids[]", id));
  }

  return await API.get(`/api/prints/by-offers/?${params.toString()}`);
};

export const getPrintStockBatchByOffers = async (offerIds = []) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (offerIds && offerIds.length > 0) {
    offerIds.forEach((id) => params.append("offer_ids[]", id));
  }

  return await API.get(`/api/print-batches/by-offers/?${params.toString()}`);
};
